import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocalization } from "../../../context/LocalizationContext";
import { useAuth } from "../../../context/AuthContext";
import { usePodSessionReport } from "../../../hooks/usePodSessionReport";
import { logSecretly } from "../../../utils/logSecretly";
import { resetPodForNextUser } from "../../../hooks/useEndSession";
import {
  asPdfBlob,
  fileNameFromUrl,
  printPdfBlob,
  saveBlobAs,
} from "../../../utils/pdfFile";
import Modal from "../Modal";
import Button from "../Button";
import "./SessionReportModal.css";

const FALLBACK_FILE_NAME = "session-report.pdf";

/**
 * The report as the built-in viewer should open it: page-width, and without
 * the viewer's own chrome, which is dead weight on a kiosk screen (the modal
 * carries the download and print buttons itself).
 *
 * Chrome honours these on an http(s) URL and ignores them on a blob:, which
 * is why the preview points at the hosted file rather than the bytes fetched
 * below. Any existing fragment is dropped; a signed URL keeps its query,
 * which is what carries the signature.
 */
const viewerUrl = (url: string): string =>
  `${url.split("#")[0]}#toolbar=0&navpanes=0&view=FitH`;

/**
 * Shows the outcome of the post-session PDF report once the pod is back on the
 * welcome screen: the wait, then the report itself (or why there isn't one).
 * Mounted app-wide — the report belongs to the session that just ended, so it
 * only ever renders while no session is live.
 */
const SessionReportModal: React.FC = () => {
  const { t } = useLocalization();
  const { session } = useAuth();
  const { report, dismiss } = usePodSessionReport();
  const [isDownloading, setIsDownloading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [frameFailed, setFrameFailed] = useState(false);

  const pdfUrl = report?.state === "READY" ? report.pdfUrl : null;

  // Guests never went through the OIDC signout redirect, so nothing has torn
  // the SPA down — which is what kept the WebSocket alive to receive this
  // report in the first place. Closing it is the end of their session, so the
  // pod is reloaded here instead, handing the next user a clean slate.
  const isGuestSession = Boolean(report?.isGuest);
  const handleClose = useCallback(() => {
    dismiss();
    if (isGuestSession) resetPodForNextUser();
  }, [dismiss, isGuestSession]);

  // Fetch the report once and keep the bytes for the print and download
  // buttons, so the file is never pulled twice. Only those two need it: the
  // preview is a plain iframe navigation, which needs no CORS. So if this
  // fails the report is still on screen — printing is what goes with it, and
  // download falls back to opening the URL.
  useEffect(() => {
    setFrameFailed(false);

    if (!pdfUrl) {
      setPdfBlob(null);
      return;
    }

    let cancelled = false;
    setPdfBlob(null);

    fetch(pdfUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return asPdfBlob(response);
      })
      .then((blob) => {
        if (!cancelled) setPdfBlob(blob);
      })
      .catch((err) => {
        logSecretly("Could not load the report bytes:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  const handleFrameError = useCallback(() => setFrameFailed(true), []);

  const printCleanupRef = useRef<(() => void) | null>(null);

  // A print dialog left open when the modal closes would otherwise strand the
  // frame and its object URL.
  useEffect(() => () => printCleanupRef.current?.(), []);

  const handlePrint = useCallback(() => {
    if (!pdfBlob) return;

    // Printing the frame is the only way to get the report on its own; if it's
    // refused, hand the file to the browser so it can be printed from its own
    // viewer instead.
    printCleanupRef.current = printPdfBlob(pdfBlob, () => {
      printCleanupRef.current = null;
      if (pdfUrl) window.open(pdfUrl, "_blank", "noopener,noreferrer");
    });
  }, [pdfBlob, pdfUrl]);

  // A live session means the previous user's report is no longer on screen.
  if (!report || session) return null;

  const handleDownload = async () => {
    if (!pdfUrl || isDownloading) return;

    const fileName = fileNameFromUrl(pdfUrl, FALLBACK_FILE_NAME);

    // Already fetched for the preview — save that copy rather than fetch twice.
    if (pdfBlob) {
      saveBlobAs(pdfBlob, fileName);
      return;
    }

    setIsDownloading(true);
    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      saveBlobAs(await asPdfBlob(response), fileName);
    } catch (err) {
      // The storage bucket doesn't have to send CORS headers, and a
      // cross-origin `download` attribute is ignored anyway — opening the
      // file directly still lets the browser save it.
      logSecretly("Falling back to opening the report URL:", err);
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } finally {
      setIsDownloading(false);
    }
  };

  const renderPreview = () => {
    // The report is shown by the browser's own PDF viewer, pointed straight at
    // the hosted file. That is a plain navigation rather than a fetch, so it
    // needs no CORS, and the viewer renders the file with the platform's own
    // fonts — the report doesn't embed its own. It scrolls inside its frame,
    // which a cross-origin page can neither drive nor shim.
    if (pdfUrl && !frameFailed) {
      return (
        <iframe
          className="session-report-frame"
          src={viewerUrl(pdfUrl)}
          title={t("sessionReport.readyTitle")}
          onError={handleFrameError}
        />
      );
    }

    return (
      <div className="session-report-preview-message">
        <p className="session-report-description">
          {t("sessionReport.previewUnavailable")}
        </p>
      </div>
    );
  };

  const renderBody = () => {
    switch (report.state) {
      case "PENDING":
        return (
          <>
            <h2 className="session-report-title">
              {t("sessionReport.pendingTitle")}
            </h2>
            <p className="session-report-description">
              {t("sessionReport.pendingDescription")}
            </p>
          </>
        );

      case "READY":
        return (
          <>
            <h2 className="session-report-title">
              {t("sessionReport.readyTitle")}
            </h2>
            <div className="session-report-preview">{renderPreview()}</div>
            <div className="session-report-actions">
              <Button
                variant="solid"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading
                  ? t("sessionReport.downloading")
                  : t("sessionReport.download")}
              </Button>
              <Button
                variant="secondary"
                onClick={handlePrint}
                disabled={!pdfBlob}
              >
                {t("sessionReport.print")}
              </Button>
            </div>
          </>
        );

      // No measurements were parsed, so the backend produced no report.
      case "UNAVAILABLE":
        return (
          <>
            <h2 className="session-report-title">
              {t("sessionReport.unavailableTitle")}
            </h2>
            <p className="session-report-description">
              {t("sessionReport.unavailableDescription")}
            </p>
          </>
        );

      // FAILED: generation errored, so there is nothing to show.
      default:
        return (
          <>
            <h2 className="session-report-title">
              {t("sessionReport.failedTitle")}
            </h2>
            <p className="session-report-description">
              {t("sessionReport.failedDescription")}
            </p>
          </>
        );
    }
  };

  return (
    <Modal isOpen onClose={handleClose} title="" message="" customLayout noScroll>
      <div className="session-report-modal-body">{renderBody()}</div>
    </Modal>
  );
};

export default SessionReportModal;
